import React, { useCallback, useEffect, useState } from 'react';
import { Currency, MeteredProduct, Rate, financeApi } from '../../api/finance';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../hooks/useToast';
import { fieldLabel, isoDay, longDate, muted, price, sectionNote, sectionTitle, select, td, th } from './shared';

const appliesFrom = (r: Rate) => (r.effectiveFrom ? `From ${longDate(r.effectiveFrom)}` : 'For all use');

/**
 * What each provider charges per use.
 *
 * Nothing is filled in for you: a price somebody guessed would make the
 * profit figure look right when it is not. A changed price is added from a
 * date, so earlier months keep the price they were paid at.
 */
export const PricesPanel: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const toast = useToast();
  const [products, setProducts] = useState<MeteredProduct[] | null>(null);
  const [ngnPerUsd, setNgnPerUsd] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MeteredProduct | null>(null);
  const [removing, setRemoving] = useState<{ product: MeteredProduct; rate: Rate } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await financeApi.rates();
      setProducts(data.products);
      setNgnPerUsd(data.ngnPerUsd);
    } catch {
      setError('Could not load provider prices.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await financeApi.removeRate(removing.rate.id);
      toast.success('Price removed.');
      setRemoving(null);
      await load();
    } catch (err) {
      toast.error(err, 'Could not remove the price.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <div style={{ color: 'var(--danger-900)', fontSize: 13 }}>{error}</div>;
  if (!products)
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner />
      </div>
    );

  const providers = Array.from(new Set(products.map((p) => p.provider)));
  const unpriced = products.filter((p) => !p.current).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h3 style={sectionTitle}>Provider prices</h3>
        <p style={sectionNote}>
          What each provider charges PayIQ per use, from their price list or invoice. PayIQ counts every use; these
          prices turn the counts into naira.{' '}
          {unpriced > 0 && `${unpriced} of ${products.length} have no price yet, and count as nothing until they do.`}{' '}
          {ngnPerUsd > 0
            ? `Dollar prices are converted at ₦${ngnPerUsd.toLocaleString('en-NG')} to $1, the card FX rate.`
            : 'No dollar rate is set, so dollar prices count as ₦0 until the card FX rate is set.'}
        </p>
      </div>

      {providers.map((provider) => {
        const rows = products.filter((p) => p.provider === provider);
        return (
          <Card key={provider} padding={0}>
            <div style={{ padding: '16px 20px 8px', fontWeight: 600, color: 'var(--neutral-900)' }}>
              {rows[0].providerLabel}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr>
                    <th style={th}>Product</th>
                    <th style={th}>Price</th>
                    <th style={th}>Earlier prices</th>
                    <th style={th} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.product}>
                      <td style={td}>{p.label}</td>
                      <td style={td}>
                        {p.current ? (
                          <>
                            <div>
                              {price(p.current.unitCost, p.current.currency)} {p.unit}
                            </div>
                            <div style={muted}>{appliesFrom(p.current)}</div>
                          </>
                        ) : (
                          <Badge variant="warning">Not priced</Badge>
                        )}
                      </td>
                      <td style={td}>
                        {p.history
                          .filter((r) => r.id !== p.current?.id)
                          .map((r) => (
                            <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 13 }}>
                              <span>
                                {price(r.unitCost, r.currency)} · {appliesFrom(r)}
                              </span>
                              {canManage && (
                                <RemoveLink onClick={() => setRemoving({ product: p, rate: r })} />
                              )}
                            </div>
                          ))}
                        {p.current && canManage && (
                          <div style={{ fontSize: 13 }}>
                            <RemoveLink
                              label="Remove current price"
                              onClick={() => setRemoving({ product: p, rate: p.current as Rate })}
                            />
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {canManage && (
                          <Button size="sm" variant="secondary" onClick={() => setEditing(p)}>
                            {p.current ? 'Change price' : 'Set price'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}

      {editing && (
        <PriceForm
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!removing}
        title="Remove this price?"
        message={
          removing
            ? `${removing.product.providerLabel} ${removing.product.label.toLowerCase()} at ${price(
                removing.rate.unitCost,
                removing.rate.currency,
              )} will stop counting. Use this for a price typed in wrong; for a price that changed, add the new one from a date instead.`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
        loading={busy}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
};

const RemoveLink: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = 'Remove' }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: 'none',
      border: 'none',
      padding: 0,
      color: 'var(--danger-700)',
      cursor: 'pointer',
      font: 'inherit',
      fontSize: 12,
    }}
  >
    {label}
  </button>
);

const PriceForm: React.FC<{
  product: MeteredProduct;
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ product, onClose, onSaved }) => {
  const toast = useToast();
  const first = !product.current;
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Currency>(product.current?.currency ?? 'NGN');
  // A first price usually applies to everything already used; a new price,
  // from the day it changed.
  const [fromStart, setFromStart] = useState(first);
  const [from, setFrom] = useState(() => isoDay(new Date()));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const value = Number(amount);
  const amountError = amount.trim() === '' || !(value >= 0) ? 'Enter a price of 0 or more.' : undefined;
  const fromError = !fromStart && !from ? 'Pick the date the price applies from.' : undefined;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (amountError || fromError) return;
    setSaving(true);
    try {
      await financeApi.setRate({
        provider: product.provider,
        product: product.product,
        unitCost: Math.round(value * 10_000) / 10_000,
        currency,
        ...(fromStart ? {} : { effectiveFrom: from }),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast.success(`${product.providerLabel} ${product.label.toLowerCase()} priced.`);
      await onSaved();
    } catch (err) {
      toast.error(err, 'Could not save the price.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`${product.providerLabel}: ${product.label}`}>
      <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, alignItems: 'end' }}>
          <Input
            label={`Price ${product.unit}`}
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={amount ? amountError : undefined}
            autoFocus
          />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={fieldLabel}>Currency</span>
            <select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} style={select}>
              <option value="NGN">Naira</option>
              <option value="USD">Dollars</option>
            </select>
          </label>
        </div>

        <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <legend style={{ ...fieldLabel, marginBottom: 8 }}>Applies to</legend>
          <label style={{ display: 'flex', gap: 8, fontSize: 14 }}>
            <input type="radio" checked={fromStart} onChange={() => setFromStart(true)} />
            All use, past and future
          </label>
          <label style={{ display: 'flex', gap: 8, fontSize: 14 }}>
            <input type="radio" checked={!fromStart} onChange={() => setFromStart(false)} />
            Use from a date on, keeping the earlier price before it
          </label>
          {!fromStart && (
            <Input type="date" aria-label="Applies from" value={from} onChange={(e) => setFrom(e.target.value)} error={fromError} />
          )}
        </fieldset>

        <Input
          label="Note (optional)"
          placeholder="Where the price came from, e.g. September invoice"
          maxLength={200}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} disabled={!!amountError || !!fromError}>
            Save price
          </Button>
        </div>
      </form>
    </Modal>
  );
};
